import { AxiosInstance, AxiosRequestConfig } from 'axios';
import { APIService } from '../api';
import { EventEmitter } from '../events';
import { Log } from '../log';
import { LogChannel } from '../log/LogChannel';
import { Roles } from '../models/Role';
import { localStorage, Storage } from '../storage';
import { checkUserDomain } from '../utils';
import { AuthenticatedUser, AuthenticatedUserFields } from './AuthenticatedUser';
import { DEFAULT_AUTH_OPTIONS, IAuthOptions } from './AuthOptions';
import { CreateUserInput } from './CreateUserInput';

export enum AUTH_EVENT {
    LOGOUT = '@auth.logout',
    LOGIN = '@auth.login',
    CHECK = '@auth.check',
    NO_LOGIN = '@auth.noLogin',
    REGISTER = '@auth.register',
}

/**
 * Handling and keep tracking of authentication actions
 */
export class AuthService {
    private static __SELF__: AuthService;
    public config: IAuthOptions = DEFAULT_AUTH_OPTIONS;
    private readonly storage: Storage;
    private _loginEndpoint: AxiosInstance | null = null;
    private _checkEndpoint: AxiosInstance | null = null;
    private _logoutEndpoint: AxiosInstance | null = null;
    private _registerEndpoint: AxiosInstance | null = null;
    private emitter: EventEmitter<AUTH_EVENT>;
    private __authenticated = false;
    private __configured = false;
    private __initialized = false;
    private __user: AuthenticatedUser | null = null;
    private __checking = false;

    private __checkingPromise!: Promise<AuthenticatedUser | null>;
    public state: Record<string, any> = {};

    get backupTokenKey(): string {
        return this.config.tokenKey + '@bk';
    }

    private log: LogChannel;

    constructor() {
        this.log = Log.channel('AuthService');
        this.storage = localStorage;
        this.emitter = new EventEmitter<AUTH_EVENT>();
        this.handleCheck = this.handleCheck.bind(this);
        this.handleLogin = this.handleLogin.bind(this);
        this.handleLogout = this.handleLogout.bind(this);
        this.getJWT = this.getJWT.bind(this);
        this.logout = this.logout.bind(this);

        this.state = this.defaultRef();

        this.on(AUTH_EVENT.LOGIN, (user: AuthenticatedUser) => {
            this.state = {
                ...this.state,
                auth: this.isAuthenticated(),
                token: this.getJWT(),
                user: user,
            };
        });

        this.on(AUTH_EVENT.CHECK, (state: boolean) => {
            this.state = {
                ...this.state,
                checking: state,
            };
        });

        this.on(AUTH_EVENT.LOGOUT, () => {
            this.state = this.defaultRef();
        });

        this.on(AUTH_EVENT.NO_LOGIN, () => {
            this.state = this.defaultRef();
        });
    }

    /**
     * Get singletone service instance
     */
    static getInstance(): AuthService {
        if (!AuthService.__SELF__) {
            AuthService.__SELF__ = new AuthService();
        }

        return AuthService.__SELF__;
    }

    /**
     * Configure service
     * @param config
     */
    public static configure(config: Partial<IAuthOptions> = {}) {
        return this.getInstance().configure(config);
    }

    get loginEndpoint() {
        if (!this._loginEndpoint) {
            this._loginEndpoint = APIService.getInstance().createEndpoint(this.config.loginPath);
        }

        return this._loginEndpoint;
    }

    get registerEndpoint() {
        if (!this._registerEndpoint) {
            this._registerEndpoint = APIService.getInstance().createEndpoint(this.config.registerPath);
        }

        return this._registerEndpoint;
    }

    get checkEndpoint() {
        if (!this._checkEndpoint) {
            this._checkEndpoint = APIService.getInstance().createEndpoint(this.config.checkPath);
        }

        return this._checkEndpoint;
    }

    get logoutEndpoint() {
        if (!this._logoutEndpoint) {
            this._logoutEndpoint = APIService.getInstance().createEndpoint(this.config.logoutPath);
        }

        return this._logoutEndpoint;
    }

    /**
     * Initialize the AuthService by adding:
     * - events to control authenticate state
     * - auth checking
     */
    public async configure(config: Partial<IAuthOptions> = {}) {
        if (this.__configured) {
            return;
        }

        this.config = { ...DEFAULT_AUTH_OPTIONS, ...config };
        this.__configured = true;

        this._loginEndpoint = null;
        this._registerEndpoint = null;
        this._checkEndpoint = null;
        this._logoutEndpoint = null;

        await this.init();
    }

    /**
     * Handle check
     * @param checking
     */
    public handleCheck(checking: boolean) {
        this.__checking = checking;
        this.state = {
            ...this.state,
            checking,
        };
    }

    /**
     *  Handle Login event
     * @param user
     */
    public handleLogin(user: AuthenticatedUser) {
        this.__authenticated = true;
        this.__user = user;
    }

    /**
     * Handle Logout event
     */
    public handleLogout() {
        this.__authenticated = false;
        this.__user = null;
    }

    /**
     * Attach an event to the emitter
     * @param key
     * @param handler
     */
    public on(key: AUTH_EVENT, handler: any) {
        return this.emitter.addEventListener(key, handler);
    }

    /**
     * Logout method
     */
    public logout() {
        this.emitter.emit(AUTH_EVENT.CHECK, true);

        return new Promise((resolve) => {
            const onEnd = () => {
                this.cleanup();
                resolve(null);
            };

            this.logoutEndpoint
                ?.get('', {
                    params: {
                        no_redirect: true,
                    },
                })
                ?.then(onEnd, onEnd);
        });
    }

    /**
     * Authenticate user using email/phone and password
     * @param identifier
     * @param password
     * @param config
     * @param withEvents
     */
    public async authenticateWithIdentifierAndPassword(
        identifier: string,
        password: string,
        config: AxiosRequestConfig = {},
        withEvents = true,
    ) {
        const { data: configData } = config;

        if (config?.data) {
            delete config.data;
        }

        config.params = config.params || {};
        if (!config.params.hasOwnProperty('with_user')) {
            config.params.with_user = true;
        }

        if (!config.params.hasOwnProperty('fields') && !config.params.hasOwnProperty('return_only')) {
            config.params.fields = AuthenticatedUserFields;
        }

        try {
            if (withEvents) {
                this.emitter.emit(AUTH_EVENT.CHECK, true);
            }
            const { data } = await this.loginEndpoint.post(
                '',
                {
                    ...configData,
                    identifier,
                    password,
                },
                config,
            );

            checkUserDomain(data);
            this.authenticateWithUserAndToken(data.user, data.token);
        } catch (e) {
            if (withEvents) {
                await this.logout();
            }
            if (e.message?.split(':')[0] === 'invalid_platform') {
                e['response'] = {
                    status: 403,
                    data: { error: e.message?.split(':')[1] },
                };
            }

            throw e;
        }
    }

    /**
     * Get JWT from the storage
     */
    public getJWT(): string {
        return this.storage.getItem(this.config.tokenKey, { encode: false }) ?? '';
    }

    /**
     * Check if we have the JWT saved in our storage
     */
    public hasJWT(): boolean {
        const jwt = this.getJWT();

        return !!jwt && jwt !== '';
    }

    /**
     * Set the JWT on our storage
     * @param token
     */
    public setJWT(token: string) {
        this.__authenticated = true;
        return this.storage.setItem(this.config.tokenKey, token, { encode: false });
    }

    /**
     * Check if the token it's a valid authentication
     */
    public async redirectIfAuth(checkViaRequest = false) {
        if (this.hasJWT()) {
            if (checkViaRequest) {
                try {
                    const response = await this.checkAuthentication();
                    if (!response) {
                        return false;
                    }
                } catch (e) {
                    return false;
                }
            }

            window.location.href = this.config.appEntry;
        }

        return false;
    }

    /**
     * Return true if an auth session it's set and a token exist
     * @return {boolean}
     */
    public isAuthenticated(): boolean {
        return this.__authenticated && this.hasJWT() && !!this.getJWT();
    }

    /**
     * Check authentication async
     */
    public async isAuthenticatedAsync(): Promise<boolean> {
        await this.checkAuthentication();
        return this.isAuthenticated();
    }

    /**
     * Return logged user
     */
    public getLoggedUser(): AuthenticatedUser | null {
        if (!this.isAuthenticated()) {
            return null;
        }

        if (!(this.__user instanceof AuthenticatedUser)) {
            this.__user = new AuthenticatedUser().from(this.__user);
        }

        return this.__user;
    }

    /**
     * Check user auth
     */
    public async checkAuthentication() {
        if (!this.hasJWT()) {
            this.__authenticated = false;
            this.__user = null;

            this.state = this.defaultRef();
            this.emitter.emit(AUTH_EVENT.NO_LOGIN);
            return null;
        }

        if (this.__checking && this.__checkingPromise) {
            return await this.__checkingPromise;
        }

        this.handleCheck(true);
        this.__checkingPromise = (async () => {
            this.emitter.emit(AUTH_EVENT.CHECK, true);

            try {
                const { data } = await this.checkEndpoint.get<AuthenticatedUser>('', {
                    params: {
                        fields: AuthenticatedUserFields,
                    },
                });

                const { user } = (data || {}) as any;
                if (user.user_view !== 'wholesaler') {
                    user.wholesaler = null;
                }
                await this.authenticateUser(user);
                return user;
            } catch (error) {
                this.log.error('checkAuthentication', { error });
                await this.logout();
            }

            return null;
        })();

        return await this.__checkingPromise;
    }

    // noinspection JSUnusedGlobalSymbols
    /**
     * Authenticate user with token
     * @param token
     */
    public async authenticateWithToken(token: string) {
        this.__authenticated = true;
        this.setJWT(token);

        if (this.__configured) {
            await this.checkAuthentication();
        } else {
            await this.init();
        }
    }

    /**
     * Reset the service
     */
    public reset() {
        this.removeJWT();

        this.__authenticated = false;
        this.__user = null;
        this.__initialized = false;
        this.__configured = false;
        this.__checking = false;

        this.emitter.removeEventListener(AUTH_EVENT.LOGIN, this.handleLogin);
        this.emitter.removeEventListener(AUTH_EVENT.LOGOUT, this.handleLogout);
        this.emitter.removeEventListener(AUTH_EVENT.CHECK, this.handleCheck);
    }

    /**
     * Return checking auth state
     */
    public isChecking(): boolean {
        return this.__checking;
    }

    /**
     * Initialize auth events and the auth checker
     */
    private async init() {
        if (this.__initialized) {
            return;
        }

        this.on(AUTH_EVENT.CHECK, this.handleCheck);
        this.on(AUTH_EVENT.LOGIN, this.handleLogin);
        this.on(AUTH_EVENT.LOGOUT, this.handleLogout);

        this.__initialized = true;

        await this.checkAuthentication();
    }

    /**
     * Remove token from the storage
     */
    private removeJWT() {
        this.storage.removeItem(this.config.tokenKey, { encode: false });
    }

    /**
     * Authenticate user
     * @param user
     */
    private authenticateUser(user: AuthenticatedUser) {
        this.actionsAfterLogin(user);
    }

    /**
     * Authenticate by user and token
     * @param user
     * @param token
     */
    private authenticateWithUserAndToken(user: AuthenticatedUser, token: string) {
        this.setJWT(token);
        this.actionsAfterLogin(user);
    }

    /**
     * Actions taken after login internals
     * @param user
     */
    private actionsAfterLogin(user: AuthenticatedUser) {
        this.__user = user;
        this.__authenticated = true;

        this.emitter.emit(AUTH_EVENT.LOGIN, user);
        this.emitter.emit(AUTH_EVENT.CHECK, false);
    }

    /**
     * Cleanup
     */
    public cleanup() {
        this.emitter.emit(AUTH_EVENT.CHECK, false);

        this.__authenticated = false;
        this.__user = null;

        this.removeJWT();

        this.emitter.emit(AUTH_EVENT.LOGOUT);
    }

    public async loginAs(userId: number) {
        this.backup();
        const { data } = await APIService.api.get(`/user-management/login-as-user/${userId}`);
        await this.authenticateWithToken(data.token);
    }

    private backup() {
        this.storage.setItem(this.backupTokenKey, this.getJWT(), { encode: false });
    }

    public hasBackup() {
        return this.storage.hasItem(this.backupTokenKey);
    }

    public async restoreBackup() {
        const jwt = this.storage.getItem(this.backupTokenKey, { encode: false });
        this.storage.removeItem(this.backupTokenKey);

        if (jwt) {
            await this.authenticateWithToken(jwt);
        }
    }

    public defaultRef() {
        return {
            auth: false,
            checking: false,
            user: null,
            token: null,
            logout: () => this.logout(),
        };
    }

    checkEmail(identifier: string) {
        return APIService.api.post('auth/email/check', { identifier });
    }

    get lastIdentifier(): string {
        return this.storage.getItem('last_auth_identifier');
    }

    set lastIdentifier(value: string) {
        this.storage.setItem('last_auth_identifier', value);
    }

    public async createUser(input: CreateUserInput) {
        if (APIService.getPlatform().getCompany() === 'apparel') {
            input.apparel_platform_user = true;
        }

        input.no_progress = true;
        input.preselected_role = input.preselected_role || Roles.CUSTOMER;

        const { data } = await this.registerEndpoint.post('', input, {
            params: {
                fields: AuthenticatedUserFields,
            },
        });

        const { data: user, token } = data;
        this.lastIdentifier = user.email;
        this.authenticateWithUserAndToken(user, token);

        return this.getLoggedUser();
    }
}
